import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldVisibility } from '../../hooks/useFieldVisibility';
import { useSettings } from '../../contexts/SettingsContext';

interface FieldVisibilityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FieldVisibilityModal: React.FC<FieldVisibilityModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { availableFields } = useFieldVisibility();
  const { settings, updateSettings } = useSettings();
  
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize selected fields from current settings
  useEffect(() => {
    if (isOpen && availableFields) {
      const currentFields = settings?.visible_fields || availableFields.optional_fields.map(f => f.key);
      setSelectedFields(currentFields);
    }
  }, [isOpen, settings?.visible_fields, availableFields]);

  const handleFieldToggle = (fieldKey: string) => {
    setSelectedFields(prev => 
      prev.includes(fieldKey) 
        ? prev.filter(key => key !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await updateSettings({ visible_fields: selectedFields });
      onClose();
    } catch (error) {
      
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    // Reset to current settings
    if (availableFields) {
      const currentFields = settings?.visible_fields || availableFields.optional_fields.map(f => f.key);
      setSelectedFields(currentFields);
    }
    onClose();
  }, [availableFields, settings?.visible_fields, onClose]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, handleClose]);

  if (!isOpen || !availableFields) return null;

  return (
    <div className="fixed inset-0 bg-black dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto border dark:border-gray-700">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {t('settings.fieldVisibility')}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              ✕
            </button>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            {t('settings.fieldVisibilityDescription')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Mandatory Fields Info */}
            <div className="border-b border-gray-200 dark:border-gray-600 pb-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('settings.mandatoryFields')}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {t('settings.mandatoryFieldsDescription')}
              </p>
              <div className="space-y-2">
                {availableFields.mandatory_fields.map(field => (
                  <div key={field.key} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`mandatory-${field.key}`}
                      checked={true}
                      disabled={true}
                      className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded opacity-50 cursor-not-allowed"
                    />
                    <label 
                      htmlFor={`mandatory-${field.key}`}
                      className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                    >
                      {t(field.name)}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Optional Fields */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('settings.optionalFields')}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {t('settings.optionalFieldsDescription')}
              </p>
              <div className="space-y-2">
                {availableFields.optional_fields.map(field => (
                  <div key={field.key} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`optional-${field.key}`}
                      checked={selectedFields.includes(field.key)}
                      onChange={() => handleFieldToggle(field.key)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <label 
                      htmlFor={`optional-${field.key}`}
                      className="ml-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                    >
                      {t(field.name)}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FieldVisibilityModal;